import React from 'react';
import PropTypes from 'prop-types';
import { includes, isEmpty, isNil, toLower } from 'lodash';
import {
  Button,
  Chip,
  Grid,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';

import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';
import { relayDeliveryMethodLabel, relayStatusLabel } from 'common/constants/relayOrders';
import { RELAY_ORDERS_PAGE_SIZE } from 'api/relay';
import { SortOrder } from 'enums/common.enum';

import formatDate from '../utils/relayDates';
import formatRelayQuantity from '../utils/relayQuantity';

/**
 * POM-6099 — relayed orders (Diamond via Quello), POM sys-admin only. Since POM-6279 the queue also
 * carries relays delivered by FAX; the Relay Type column says which. The relay's own reference
 * (Quello order id / TX- number) is not a column; the detail dialog shows it. Twelve columns
 * overflow a narrow screen; TableContainer scrolls horizontally rather than wrapping every cell.
 *
 * WHAT IS DELIBERATELY NOT A COLUMN HERE:
 *
 * - The ship-to address. It is PHI, it is the one operational PHI field the detail view exposes,
 *   and a list column is the wrong place for it: a table is the thing people copy, screenshot and
 *   export, and one careless export turns a column into a file of patient addresses on somebody's
 *   desktop. It belongs in the dialog, for one order at a time.
 * - Tracking number, carrier and tracking URL. Story 8 shipped, so these are no longer empty on
 *   every row — but they still do not belong here, for two reasons that outlast the original one.
 *   First, the queue's question is "did this order reach Diamond", and the answer is the status;
 *   tracking answers "where is the parcel", which is a per-order question the dialog handles.
 *   Second, `app/src/api/relay/__tests__/relayQueries.test.js` pins the queue's selection set with
 *   `toEqual`, so adding a column means deliberately removing a guard rather than extending one.
 *   (The CSV-export hazard that applies to the ship-to address does NOT apply here — this is a
 *   plain MUI Table with no export affordance — but see FillHistoryGrid, where tracking IS in an
 *   AG Grid that exports every column by default.)
 *
 * THE PATIENT'S NAME IS NOW SHOWN, reversing the above (PO instruction, 2026-09-22). The reasoning
 * it replaces is worth keeping in view: a list is the artefact people export and screenshot, and a
 * NAME is more identifying than the patient MPI this queue still refuses to select. It is here
 * because the central fill queue already shows patientName beside drugName for a WIDER audience
 * than this POM-sys-admin-only view, so relay was the outlier rather than the standard.
 *
 * The query asks for `patientName` INSTEAD of `patientId`, never both: core resolves the name from
 * the MPI through a join, so the identifier that correlates this patient across systems never
 * reaches the browser at all.
 */
const HEAD_CELLS = [
  { id: 'externalOrderId', label: 'Reference' },
  { id: 'patientName', label: 'Patient', sortable: true },
  { id: 'partnerName', label: 'Partner', sortable: true },
  { id: 'drugName', label: 'Drug Name' },
  // NDC hidden for now (POM-6279 QA).
  { id: 'quantity', label: 'Qty' },
  { id: 'lineItemCount', label: 'Items' },
  { id: 'status', label: 'Status' },
  { id: 'deliveryMethod', label: 'Relay Type', sortable: true },
  { id: 'fulfillingPharmacyName', label: 'Fulfilling Pharmacy', sortable: true },
  { id: 'transferredAt', label: 'Transferred At', sortable: true },
];

// PENDING (shown "In Process") is still being sent; TRANSFERRED has reached the relay pharmacy.
const STATUS_COLOR = {
  PENDING: 'info',
  TRANSFERRED: 'success',
};

/**
 * POM-6279. What each column renders, keyed by HEAD_CELLS id, so a hidden column drops its header
 * and its cell together and the two can never drift out of step.
 */
const CELL_CONTENT = {
  externalOrderId: (order) => order.externalOrderId,
  // Both resolved by a LEFT JOIN in core, so null is a real answer -- an MPI with no patient row,
  // or a partner id with no partner row -- and not an error.
  patientName: (order) => order.patientName || 'N/A',
  partnerName: (order) => order.partnerName || 'N/A',
  drugName: (order) => order.drugName || 'N/A',
  quantity: (order) => formatRelayQuantity(order.quantity),
  // Shown even though the MVP is single-item: core reports the real count, and a multi-item order
  // whose first drug was rendered as the whole order would be a silent misstatement of what was
  // dispensed.
  lineItemCount: (order) => order.lineItemCount,
  status: (order) => (
    <Chip
      size='small'
      label={relayStatusLabel(order.status)}
      color={STATUS_COLOR[order.status] || 'default'}
      sx={{ fontWeight: 600 }}
    />
  ),
  deliveryMethod: (order) => relayDeliveryMethodLabel(order.deliveryMethod),
  // Core resolves the name from the fax snapshot, else the pharmacy's current name; null only
  // when neither exists, and then the id is the fallback.
  fulfillingPharmacyName: (order) =>
    order.fulfillingPharmacyName || order.fulfillingPharmacyId || 'N/A',
  transferredAt: (order) => formatDate(order.transferredAt),
};

/**
 * POM-6279 — the relay orders table, presentational only. Shared by the Central Fill queue's
 * Relay tab (RelayOrdersTable, which owns the redux paging) and the patient page's Relay Orders
 * tab (which owns local paging and hides the Patient column, since every row is that patient).
 *
 * `rows` undefined means loading; an empty array means nothing was found, worded by
 * `emptyMessage` when the caller's scope needs saying (the patient tab's read is TRANSFERRED-only).
 */
const RelayOrdersTableView = ({
  rows,
  count,
  page,
  onPageChange,
  onOpenDetails,
  hiddenColumns,
  emptyMessage,
  sortBy,
  onSortChange,
}) => {
  // POM-6279: sorting is server-side, so only a caller that refetches passes onSortChange.
  const handleSort = (sortKey) =>
    onSortChange({
      sortKey,
      sortOrder:
        sortBy?.sortKey === sortKey && sortBy?.sortOrder === SortOrder.ASCENDING
          ? SortOrder.DESCENDING
          : SortOrder.ASCENDING,
    });

  const renderHeader = (cell) => {
    if (!cell.sortable || !onSortChange) return cell.label;

    const isActive = sortBy?.sortKey === cell.id;

    return (
      <TableSortLabel
        active={isActive}
        direction={isActive ? toLower(sortBy.sortOrder) : 'asc'}
        onClick={() => handleSort(cell.id)}
      >
        {cell.label}
      </TableSortLabel>
    );
  };

  const visibleCells = HEAD_CELLS.filter((cell) => !includes(hiddenColumns, cell.id));
  const totalPages = Math.ceil(count / RELAY_ORDERS_PAGE_SIZE);

  return (
    <Grid container direction='column'>
      <Grid item>
        <TableContainer>
          <Table size='small' aria-label='relayed orders'>
            <TableHead>
              <TableRow>
                {visibleCells.map((cell) => (
                  <TableCell key={cell.id}>{renderHeader(cell)}</TableCell>
                ))}
                <TableCell align='right'>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(rows || []).map((order) => (
                <TableRow key={order.relayOrderId} hover>
                  {visibleCells.map((cell) => (
                    <TableCell key={cell.id}>{CELL_CONTENT[cell.id](order)}</TableCell>
                  ))}
                  <TableCell align='right'>
                    {/* Labelled by relay order, NOT by reference: a split order relays each portion
                        separately, so several rows can share one externalOrderId. */}
                    <Button
                      size='small'
                      onClick={() => onOpenDetails(order.relayOrderId)}
                      aria-label={`view details for relay order ${order.relayOrderId}`}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            {totalPages > 1 && (
              <TableFooter>
                <TableRow>
                  <TableCell
                    colSpan={visibleCells.length + 1}
                    align='right'
                    sx={{ borderBottom: 'none' }}
                  >
                    <Pagination
                      sx={{ justifyContent: 'flex-end', alignItems: 'flex-end', display: 'flex' }}
                      count={totalPages}
                      page={page}
                      size='small'
                      onChange={(_event, next) => onPageChange(next)}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Grid>

      {isNil(rows) && (
        <Grid item>
          <LoadingModule />
        </Grid>
      )}

      {!isNil(rows) && isEmpty(rows) && (
        <Grid item>
          <NothingFound customMessage={emptyMessage} />
        </Grid>
      )}
    </Grid>
  );
};

RelayOrdersTableView.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({ relayOrderId: PropTypes.string.isRequired })),
  count: PropTypes.number,
  page: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onOpenDetails: PropTypes.func.isRequired,
  hiddenColumns: PropTypes.arrayOf(PropTypes.string),
  emptyMessage: PropTypes.string,
  sortBy: PropTypes.shape({ sortKey: PropTypes.string, sortOrder: PropTypes.string }),
  onSortChange: PropTypes.func,
};

RelayOrdersTableView.defaultProps = {
  rows: undefined,
  count: 0,
  hiddenColumns: [],
  emptyMessage: null,
  sortBy: null,
  onSortChange: null,
};

export default RelayOrdersTableView;
